import React, { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ["text", "textarea", "number", "date", "email", "phonenumber",
  "radiobuttons", "checkboxes", "picklist", "grid", "plaintext"];
const CHOICE_TYPES = new Set(["radiobuttons", "checkboxes", "picklist"]);

function singleTerm(cond) {
  const m = (cond || "").trim().match(/^([A-Za-z_][A-Za-z0-9_]*)\s*==\s*"([^"]*)"$/);
  return m ? { field: m[1], value: m[2] } : null;
}

export default function GestionQuestionnaireEntrevueModal({ onClose, showPopup }) {
  const [schema, setSchema] = useState(null);
  const [selSec, setSelSec] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/fir-schema`, { withCredentials: true })
      .then(r => setSchema(r.data))
      .catch(e => showPopup?.(`Erreur de chargement du schéma : ${e.response?.data?.detail || e.message}`, "error", "Questionnaire d'entrevue"));
  }, [showPopup]);

  if (!schema) {
    return (<div style={ov}><div style={{ ...box, width: 480, padding: 24 }} data-testid="gestion-qentrevue-modal"><p>Chargement du schéma…</p></div></div>);
  }

  const sections = schema.sections || [];
  const rules = schema.rules || [];

  // toutes les questions de l'entrevue (pour résoudre les libellés)
  const allQuestions = [];
  sections.forEach(s => (s.fields || []).forEach(f => {
    allQuestions.push({ name: f.name, label: f.displayname || f.name, type: f.type, values: f.values || [], section: s.displayname || s.name });
  }));

  // questions d'une section (pour les listes déroulantes ciblées)
  const questionsOfSection = (secIdx) => (sections[secIdx]?.fields || []).map(f =>
    ({ name: f.name, label: f.displayname || f.name, type: f.type, values: f.values || [] }));

  const setRules = (next) => setSchema(s => ({ ...s, rules: next }));
  const setSections = (next) => setSchema(s => ({ ...s, sections: next }));

  // conditions « AFFICHER SI » pour un item (nom de section ou de question) :
  const showCondsFor = (itemName) => rules
    .map(r => ({ r, t: singleTerm(r.condition) }))
    .filter(x => x.t && (x.r.show || []).includes(itemName))
    .map(x => ({ q: x.t.field, v: x.t.value }));

  const addShowCond = (itemName, q, v) => {
    if (!q || v === "" || q === itemName) return;
    const cond = `${q} == "${v}"`;
    let found = false;
    let next = rules.map(r => {
      if (r.condition === cond && (r.hide || []).length === 0) {
        found = true; const sh = r.show || []; return sh.includes(itemName) ? r : { ...r, show: [...sh, itemName] };
      }
      return r;
    });
    if (!found) next = [...next, { condition: cond, show: [itemName], hide: [] }];
    setRules(next);
  };
  const removeShowCond = (itemName, q, v) => {
    const cond = `${q} == "${v}"`;
    setRules(rules.map(r => r.condition === cond ? { ...r, show: (r.show || []).filter(x => x !== itemName) } : r)
      .filter(r => !((r.hide || []).length === 0 && (r.show || []).length === 0)));
  };

  // ---------- sections ----------
  const moveSection = (i, d) => { const j = i + d; if (j < 0 || j >= sections.length) return; const n = [...sections]; [n[i], n[j]] = [n[j], n[i]]; setSections(n); setSelSec(j); };
  const addSection = () => { setSections([...sections, { name: `Section_${Date.now()}`, displayname: "Nouvelle section", fields: [] }]); setSelSec(sections.length); };
  const delSection = (i) => { if (!window.confirm("Supprimer cette section et ses questions ?")) return; const n = sections.filter((_, k) => k !== i); setSections(n); setSelSec(Math.max(0, Math.min(selSec, n.length - 1))); };
  const updSection = (i, patch) => { const n = [...sections]; n[i] = { ...n[i], ...patch }; setSections(n); };

  // ---------- questions (par index de section) ----------
  const setFields = (secIdx, fields) => updSection(secIdx, { fields });
  const moveField = (secIdx, i, d) => { const f = sections[secIdx].fields || []; const j = i + d; if (j < 0 || j >= f.length) return; const n = [...f]; [n[i], n[j]] = [n[j], n[i]]; setFields(secIdx, n); };
  const addField = (secIdx) => setFields(secIdx, [{ name: `Q_${Date.now()}`, displayname: "Nouvelle question", type: "text", mandatory: false }, ...(sections[secIdx].fields || [])]);
  const delField = (name) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    setSchema(s => ({
      ...s,
      sections: s.sections.map(x => ({ ...x, fields: (x.fields || []).filter(f => f.name !== name) })),
      rules: s.rules.map(r => ({ ...r, show: (r.show || []).filter(n => n !== name), hide: (r.hide || []).filter(n => n !== name) }))
        .filter(r => !((r.hide || []).length === 0 && (r.show || []).length === 0 && singleTerm(r.condition) && r.condition.startsWith(name + " ")))
    }));
  };
  const updField = (name, patch) => setSchema(s => ({ ...s, sections: s.sections.map(x => ({ ...x, fields: (x.fields || []).map(f => f.name === name ? { ...f, ...patch } : f) })) }));
  const moveFieldToSection = (name, targetSecIdx) => setSchema(s => {
    const src = s.sections.findIndex(x => (x.fields || []).some(f => f.name === name));
    if (src < 0) return s;
    const field = s.sections[src].fields.find(f => f.name === name);
    return { ...s, sections: s.sections.map((x, si) => si === src ? { ...x, fields: x.fields.filter(f => f.name !== name) } : si === targetSecIdx ? { ...x, fields: [...(x.fields || []), field] } : x) };
  });

  const scrollToSection = (i) => { setSelSec(i); document.getElementById(`sec-block-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  const save = async () => {
    setSaving(true);
    try {
      // Questions en mode « Afficher si » → cachées par défaut : recalcul de
      // default_hidden = tous les items référencés par une condition show.
      const shown = new Set();
      rules.forEach(r => { if (singleTerm(r.condition)) (r.show || []).forEach(n => shown.add(n)); });
      // nettoyage des réponses possibles : on retire les lignes vides à l'enregistrement.
      const cleanSections = sections.map(sec => ({
        ...sec,
        fields: (sec.fields || []).map(f => Array.isArray(f.values)
          ? { ...f, values: f.values.map(v => v.trim()).filter(Boolean) } : f)
      }));
      await axios.put(`${API}/admin/fir-schema`, { ...schema, sections: cleanSections, default_hidden: [...shown] }, { withCredentials: true });
      showPopup?.("Questionnaire d'entrevue enregistré.", "success", "Questionnaire d'entrevue");
    } catch (e) {
      showPopup?.(`Erreur d'enregistrement : ${e.response?.data?.detail || e.message}`, "error", "Questionnaire d'entrevue");
    } finally { setSaving(false); }
  };

  return (
    <div style={ov}>
      <div style={box} data-testid="gestion-qentrevue-modal">
        <div style={titlebar}>
          <span>GESTION DU QUESTIONNAIRE D'ENTREVUE</span>
          <button onClick={onClose} style={xbtn} data-testid="qentrevue-close">×</button>
        </div>
        <div style={{ background: "#eef5ff", color: "#1e5aa8", fontSize: 12, padding: "5px 12px", borderBottom: "1px solid #cfe0f5" }}>
          <b>Sections</b> : affichées par défaut — « <b>Afficher si…</b> » utilise une réponse de la <b>section précédente</b>. &nbsp;|&nbsp;
          <b>Questions</b> : « <b>Afficher si…</b> » utilise une réponse d'une question de la <b>même section</b> (sinon toujours affichée).
        </div>

        <div style={{ flex: 1, overflow: "hidden", padding: 12, display: "flex", gap: 12 }}>
          {/* navigateur de sections */}
          <div style={{ flex: "0 0 260px", borderRight: "1px solid #ccc", paddingRight: 10, overflow: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <b>Sections ({sections.length})</b>
              <button style={miniBtn} onClick={addSection} data-testid="qentrevue-add-section">+ Section</button>
            </div>
            {sections.map((s, i) => (
              <div key={s.name + i} onClick={() => scrollToSection(i)} data-testid={`qentrevue-section-${i}`}
                style={{ padding: "5px 6px", marginBottom: 3, cursor: "pointer", borderRadius: 4, fontSize: 13, background: i === selSec ? "#d6e4ff" : "#f5f7fb", border: "1px solid #dde3ec" }}>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <span style={{ flex: 1, fontWeight: 600 }}>{i + 1}. {s.displayname || s.name}</span>
                  {showCondsFor(s.name).length > 0 && <span title="Section conditionnelle" style={{ color: "#a15c00" }}>⊘</span>}
                  <button style={arrowBtn} onClick={e => { e.stopPropagation(); moveSection(i, -1); }}>↑</button>
                  <button style={arrowBtn} onClick={e => { e.stopPropagation(); moveSection(i, 1); }}>↓</button>
                  <button style={{ ...arrowBtn, color: "#b00" }} onClick={e => { e.stopPropagation(); delSection(i); }}>✕</button>
                </div>
                <span style={{ fontSize: 11, color: "#888" }}>{(s.fields || []).length} question(s)</span>
              </div>
            ))}
          </div>

          {/* seulement la section sélectionnée */}
          <div style={{ flex: 1, overflow: "auto" }} data-testid="qentrevue-structure">
            {sections.length === 0 && <p style={{ color: "#888" }}>Aucune section. Ajoutez-en une.</p>}
            {sections.filter((_, i) => i === selSec).map((sec) => {
              const secIdx = selSec;
              const prevSectionChoices = sections.slice(0, secIdx).map((s, si) => ({ value: si, label: `${si + 1}. ${s.displayname || s.name}` }));
              return (
                <div key={sec.name + secIdx} id={`sec-block-${secIdx}`} style={secBlock} data-testid={`qentrevue-secblock-${secIdx}`}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontWeight: 800, color: "#1e5aa8", whiteSpace: "nowrap" }}>Section {secIdx + 1}</span>
                    <input style={{ ...inp, flex: 1, fontWeight: 600 }} value={sec.displayname || ""} data-testid={`qentrevue-section-name-${secIdx}`}
                      onChange={e => updSection(secIdx, { displayname: e.target.value })} />
                    <button style={arrowBtn} onClick={() => moveSection(secIdx, -1)}>↑</button>
                    <button style={arrowBtn} onClick={() => moveSection(secIdx, 1)}>↓</button>
                    <button style={{ ...arrowBtn, color: "#b00" }} onClick={() => delSection(secIdx)}>✕</button>
                  </div>

                  <CondEditor title="Afficher cette section si :" testid={`qentrevue-show-section-${secIdx}`}
                    conds={showCondsFor(sec.name)} allQuestions={allQuestions} selfName={sec.name}
                    addLabel="+ Afficher si"
                    sectionChoices={prevSectionChoices}
                    questionsForSection={(si) => questionsOfSection(si)}
                    emptyMsg={showCondsFor(sec.name).length === 0 ? "Toujours affichée (aucune condition)." : null}
                    noOptionsMsg="Aucune section précédente : la première section est toujours affichée."
                    onAdd={(q, v) => addShowCond(sec.name, q, v)} onRemove={(q, v) => removeShowCond(sec.name, q, v)} />

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "10px 0 6px" }}>
                    <b style={{ fontSize: 13 }}>Questions ({(sec.fields || []).length})</b>
                    <button style={miniBtn} onClick={() => addField(secIdx)} data-testid={`qentrevue-add-question-${secIdx}`}>+ Question</button>
                  </div>

                  {(sec.fields || []).length === 0 && <div style={{ fontSize: 12, color: "#999", paddingLeft: 4 }}>Aucune question dans cette section.</div>}

                  {(sec.fields || []).map((f, i) => {
                    const questionShowOptions = questionsOfSection(secIdx).filter(x => x.name !== f.name);
                    return (
                      <div key={f.name + i} style={qCard} data-testid={`qentrevue-question-${secIdx}-${i}`}>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                          <span style={{ fontWeight: 800, color: "#1e5aa8", whiteSpace: "nowrap", minWidth: 34 }} data-testid={`qentrevue-num-${secIdx}-${i}`}>{secIdx + 1}.{i + 1}</span>
                          <input style={{ ...inp, flex: 1 }} value={f.displayname || ""} placeholder="Libellé de la question"
                            data-testid={`qentrevue-label-${f.name}`} onChange={e => updField(f.name, { displayname: e.target.value })} />
                          <button style={arrowBtn} onClick={() => moveField(secIdx, i, -1)}>↑</button>
                          <button style={arrowBtn} onClick={() => moveField(secIdx, i, 1)}>↓</button>
                          <button style={{ ...arrowBtn, color: "#b00" }} onClick={() => delField(f.name)} data-testid={`qentrevue-del-${f.name}`}>✕</button>
                        </div>
                        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                          <select style={{ ...inp, width: 140 }} value={f.type || "text"} data-testid={`qentrevue-type-${f.name}`} onChange={e => updField(f.name, { type: e.target.value })}>
                            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <label style={{ fontSize: 13, display: "flex", gap: 5, alignItems: "center" }}>
                            <input type="checkbox" checked={!!f.mandatory} onChange={e => updField(f.name, { mandatory: e.target.checked })} />
                            Obligatoire
                          </label>
                          {f.type === "checkboxes" && (
                            <label style={{ fontSize: 13, display: "flex", gap: 5, alignItems: "center" }} title="Ajoute un champ montant ($, sans décimales) sous chaque réponse cochée">
                              <input type="checkbox" checked={!!f.demande_montant} data-testid={`qentrevue-montant-${f.name}`}
                                onChange={e => updField(f.name, { demande_montant: e.target.checked })} />
                              Demander un montant ($)
                            </label>
                          )}
                          <select style={{ ...inp, width: 180, marginLeft: "auto" }} value="" data-testid={`qentrevue-move-${f.name}`}
                            onChange={e => { if (e.target.value !== "") moveFieldToSection(f.name, parseInt(e.target.value)); }} title="Déplacer vers une autre section">
                            <option value="">Déplacer vers une section…</option>
                            {sections.map((s, si) => si !== secIdx ? <option key={si} value={si}>{si + 1}. {s.displayname || s.name}</option> : null)}
                          </select>
                        </div>
                        {CHOICE_TYPES.has(f.type) && (
                          <div style={{ marginTop: 4 }}>
                            <label style={lbl}>Réponses possibles (une par ligne)</label>
                            <textarea style={{ ...inp, height: 48 }} value={(f.values || []).join("\n")}
                              onChange={e => updField(f.name, { values: e.target.value.split("\n") })} />
                          </div>
                        )}
                        <div style={{ marginTop: 4 }}>
                          <label style={lbl} title="Ce texte s'affichera dans une bulle d'aide (ⓘ) à côté de la question durant l'entrevue.">
                            Bulle d'explication (facultatif)
                          </label>
                          <textarea style={{ ...inp, height: 48 }} value={f.aide || ""}
                            placeholder="Texte d'aide affiché dans une infobulle (ⓘ) à côté de la question"
                            data-testid={`qentrevue-aide-${f.name}`}
                            onChange={e => updField(f.name, { aide: e.target.value })} />
                        </div>
                        <CondEditor title="Afficher cette question si :" testid={`qentrevue-show-${f.name}`}
                          conds={showCondsFor(f.name)} allQuestions={allQuestions} options={questionShowOptions} selfName={f.name}
                          addLabel="+ Afficher si"
                          emptyMsg={showCondsFor(f.name).length === 0 ? "Toujours affichée (aucune condition)." : null}
                          noOptionsMsg="Aucune autre question dans cette section."
                          onAdd={(q, v) => addShowCond(f.name, q, v)} onRemove={(q, v) => removeShowCond(f.name, q, v)} />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "10px 14px", borderTop: "1px solid #ccc", background: "#eef2f8" }}>
          <button style={btnGrey} onClick={onClose} data-testid="qentrevue-cancel">Annuler</button>
          <button style={btnBlue} onClick={save} disabled={saving} data-testid="qentrevue-save">{saving ? "…" : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
}

function CondEditor({ title, testid, conds, allQuestions, options, selfName, onAdd, onRemove, addLabel, emptyMsg, noOptionsMsg, sectionChoices, questionsForSection }) {
  const [q, setQ] = useState("");
  const [v, setV] = useState("");
  const hasSectionPicker = Array.isArray(sectionChoices) && sectionChoices.length > 0;
  const [secChoice, setSecChoice] = useState(hasSectionPicker ? String(sectionChoices[sectionChoices.length - 1].value) : "");
  const labelOf = (name) => (allQuestions.find(x => x.name === name)?.label) || name;
  const baseOptions = hasSectionPicker
    ? (questionsForSection && secChoice !== "" ? questionsForSection(parseInt(secChoice)) : [])
    : (options || []);
  const opts = baseOptions.filter(x => x.name !== selfName);
  const trig = opts.find(x => x.name === q);
  const isChoice = trig && CHOICE_TYPES.has(trig.type) && (trig.values || []).length > 0;
  // Pour l'éditeur de section : on montre toujours le sélecteur de section s'il
  // existe des sections précédentes, même si la section choisie n'a pas de question.
  const showControls = hasSectionPicker || opts.length > 0;
  return (
    <div style={{ marginTop: 6, background: "#fbfaf3", border: "1px dashed #d8caa0", borderRadius: 5, padding: 6 }} data-testid={testid}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#7a5a00", marginBottom: 3 }}>{title}</div>
      {conds.length === 0 && <div style={{ fontSize: 11, color: "#999", marginBottom: 3 }}>{emptyMsg || "Aucune condition."}</div>}
      {conds.map((c, k) => (
        <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginBottom: 2 }}>
          <span>• Si « <b>{labelOf(c.q)}</b> » = « <b>{c.v}</b> »</span>
          <button style={{ ...arrowBtn, color: "#b00", padding: "0 5px" }} onClick={() => onRemove(c.q, c.v)}>✕</button>
        </div>
      ))}
      {!showControls ? (
        <div style={{ fontSize: 11, color: "#999", marginTop: 3 }}>{noOptionsMsg || "Aucune question disponible."}</div>
      ) : (
        <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
          {hasSectionPicker && (
            <select style={{ ...inp, width: 200 }} value={secChoice} data-testid={`${testid}-section-select`}
              onChange={e => { setSecChoice(e.target.value); setQ(""); setV(""); }}>
              {sectionChoices.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          )}
          {opts.length === 0 ? (
            <span style={{ fontSize: 11, color: "#999" }}>Aucune question dans cette section.</span>
          ) : (<>
          <select style={{ ...inp, width: 220 }} value={q} onChange={e => { setQ(e.target.value); setV(""); }}>
            <option value="">— question déclencheuse —</option>
            {opts.map(x => <option key={x.name} value={x.name}>{x.label}</option>)}
          </select>
          {isChoice ? (
            <select style={{ ...inp, width: 150 }} value={v} onChange={e => {
              const val = e.target.value;
              setV(val);
              if (q && val !== "") { onAdd(q, val); setQ(""); setV(""); }
            }}>
              <option value="">— réponse —</option>
              {trig.values.map(val => <option key={val} value={val}>{val}</option>)}
            </select>
          ) : (
            <input style={{ ...inp, width: 150 }} placeholder="réponse (Entrée)" value={v}
              onChange={e => setV(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && q && v !== "") { onAdd(q, v); setQ(""); setV(""); } }} />
          )}
          <button style={miniBtn} disabled={!q || v === ""} onClick={() => { onAdd(q, v); setQ(""); setV(""); }}>{addLabel || "+ Ajouter"}</button>
          </>)}
        </div>
      )}
    </div>
  );
}

const ov = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1400 };
const box = { background: "#f3f5f9", width: "94vw", maxWidth: 1200, height: "88vh", display: "flex", flexDirection: "column", border: "1px solid #7a8aa0", boxShadow: "3px 3px 10px rgba(0,0,0,0.35)" };
const titlebar = { background: "linear-gradient(180deg,#1e5aa8,#164a8f)", color: "#fff", padding: "8px 12px", fontWeight: 700, fontSize: 14, display: "flex", justifyContent: "space-between", alignItems: "center" };
const xbtn = { background: "transparent", border: "none", color: "#fff", fontSize: 20, cursor: "pointer" };
const lbl = { display: "block", fontSize: 11, color: "#556", fontWeight: 600, marginBottom: 2 };
const inp = { width: "100%", padding: "4px 6px", border: "1px solid #b7c0d0", borderRadius: 4, fontSize: 13, boxSizing: "border-box" };
const qCard = { border: "1px solid #000", borderRadius: 6, padding: 8, marginBottom: 8, background: "#fff", marginLeft: 12 };
const secBlock = { border: "1px solid #c6d2e2", borderRadius: 8, padding: 12, marginBottom: 14, background: "#f7f9fc" };
const miniBtn = { background: "#1e5aa8", color: "#fff", border: "none", borderRadius: 4, padding: "4px 10px", fontSize: 12, cursor: "pointer", fontWeight: 600 };
const arrowBtn = { background: "#e4e9f0", border: "1px solid #c3ccda", borderRadius: 3, cursor: "pointer", fontSize: 12, padding: "1px 6px" };
const btnBlue = { background: "#1e5aa8", color: "#fff", border: "none", borderRadius: 4, padding: "7px 18px", fontWeight: 700, cursor: "pointer" };
const btnGrey = { background: "#dfe4ec", color: "#333", border: "1px solid #b7c0d0", borderRadius: 4, padding: "7px 16px", cursor: "pointer" };
